import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { 
  TextField,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  Tooltip,
  IconButton,
  Paper
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { CREATE_QR_CODE_MUTATION } from 'globals/graphql';

import { black, grayLight } from "design-system/colors";
import { QRCodeCreationModalProps, QRCodeFormState } from '../types';
import MoovsDialog from 'components/MoovsDialog';
import { useAnalytics, useSnackbar } from 'globals/hooks';

const initialFormState: QRCodeFormState = {
  displayName: '',
  baseUrl: '',
  utmCampaignId: '',
  utmCampaignName: '',
  utmSource: '',
  utmTerm: '',
  isCustomUrlMode: false,
};

function QRCodeCreationModal(props: QRCodeCreationModalProps) {
  const { open, onClose, onSuccess } = props;

  // hooks
  const { track } = useAnalytics();
  const snackbar = useSnackbar();

  // state
  const [formState, setFormState] = useState<QRCodeFormState>(initialFormState);

  // mutations
  const [createQRCode, { loading }] = useMutation(CREATE_QR_CODE_MUTATION, {
    onCompleted: (data) => {
      if (data.createQrCode.result.__typename === 'QRCode') {
        onSuccess();
        track(formState.isCustomUrlMode ? 'qrCodeApp_created_customUrl' : 'qrCodeApp_created_basicUrl');
        setFormState(initialFormState);
      } else if (data.createQrCode.result.__typename === 'CreateQrCodeFailure') {
        console.error(data.createQrCode.result.message);
        snackbar.error(data.createQrCode.result.message);
      }
    },
    onError: (error) => {
      console.error(error);
      snackbar.error(error);
    },
  });

  // derived state
  const isCreateButtonDisabled = !formState.displayName || !formState.baseUrl || loading;

  // event handlers
  const handleSubmit = () => {    
    const input = {
      displayName: formState.displayName,
      baseUrl: formState.baseUrl,
      ...(formState.isCustomUrlMode && formState.utmCampaignId ? { utmCampaignId: formState.utmCampaignId } : {}),
      ...(formState.isCustomUrlMode && formState.utmCampaignName ? { utmCampaignName: formState.utmCampaignName } : {}),
      ...(formState.isCustomUrlMode && formState.utmSource ? { utmSource: formState.utmSource } : {}),
      ...(formState.isCustomUrlMode && formState.utmTerm ? { utmTerm: formState.utmTerm } : {}),
    };

    createQRCode({
      variables: {
        input,
      },
    });
  };

  const handleChange = (field: keyof QRCodeFormState) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'isCustomUrlMode' ? event.target.checked : event.target.value;
    setFormState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleClose = () => {
    setFormState(initialFormState);
    onClose();
  };

  // helpers
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(getFullUrl());
  };

  const getFullUrl = () => {
    if (!formState.baseUrl) return '';
    
    const params = [];
    
    if (formState.isCustomUrlMode) {
      if (formState.utmCampaignId) params.push(`utm_id=${encodeURIComponent(formState.utmCampaignId).replace(/\+/g, '%20')}`);
      if (formState.utmCampaignName) params.push(`utm_campaign=${encodeURIComponent(formState.utmCampaignName).replace(/\+/g, '%20')}`);
      if (formState.utmSource) params.push(`utm_source=${encodeURIComponent(formState.utmSource).replace(/\+/g, '%20')}`);
      if (formState.utmTerm) params.push(`utm_term=${encodeURIComponent(formState.utmTerm).replace(/\+/g, '%20')}`);
    }
    
    return params.length > 0 
      ? `${formState.baseUrl}?${params.join('&')}`
      : formState.baseUrl;
  };

  return (
    <MoovsDialog
      dialogTitle="Create a New QR Code"
      closeButtonText="Cancel"
      acceptButtonText="Generate"
      open={open}
      onClose={handleClose}
      onAccept={handleSubmit}
      acceptDisabled={isCreateButtonDisabled}
      size="sm"
      hideLoadingIndicator
    >
      <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formState.isCustomUrlMode}
              onChange={handleChange('isCustomUrlMode')}
              sx={{ mr: 3 }}
            />
          }
          label="UTM Parameters"
          sx={{ ml: 0, mr: 0 }}
        />
        <Tooltip title="Add campaign parameters to URLs so you can measure them in Custom Dashboards">
          <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <TextField
        label="Your link"
        fullWidth
        value={formState.baseUrl}
        onChange={handleChange('baseUrl')}
        margin="dense"
        required
        placeholder="https://example.com"
      />

      {formState.isCustomUrlMode && (
        <>
          <TextField
            label="Campaign ID"
            fullWidth
            value={formState.utmCampaignId}
            onChange={handleChange('utmCampaignId')}
            margin="dense"
          />

          <TextField
            label="Campaign Name"
            fullWidth
            value={formState.utmCampaignName}
            onChange={handleChange('utmCampaignName')}
            margin="dense"
          />

          <TextField
            label="Source"
            fullWidth
            value={formState.utmSource}
            onChange={handleChange('utmSource')}
            margin="dense"
          />

          <TextField
            label="Term"
            fullWidth
            value={formState.utmTerm}
            onChange={handleChange('utmTerm')}
            margin="dense"
          />
        </>
      )}

      {formState.baseUrl && formState.isCustomUrlMode && (
        <Paper 
          elevation={0}
          sx={{ 
            mt: 2, 
            mb: 2, 
            p: 1.5, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            overflowX: 'auto',
            wordBreak: 'break-all',
            backgroundColor: grayLight,
            flexShrink: 0,
            height: 'auto'
          }}
        >
          <Typography 
            variant="subtitle2" 
            sx={{ 
              mr: 1, 
              color: black,
            }}
          >
            {getFullUrl()}
          </Typography>
          <IconButton 
            size="small" 
            onClick={handleCopyUrl} 
            sx={{ 
              flexShrink: 0,
              color: '#565656',
              '&:hover': {
                backgroundColor: grayLight,
                color: '#195FE9',
              }
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Paper>
      )}

      <TextField
        label="QR Name"
        fullWidth
        value={formState.displayName}
        onChange={handleChange('displayName')}
        margin="dense"
        required
      />
    </MoovsDialog>
  );
}; 

export default QRCodeCreationModal;